/**
 * タスク一覧。承認完了で起票された「自社の手作業」「自動(コネクタ)」「社外依頼」。
 *
 * 一覧テンプレート(`ui/`)。表は 7 列まで(# / 申請・対象者 / 内容 / 担当 / 状態 / 実行予定日 / 操作)。
 * 割当種別・生成トリガー・方式・起票・完了・記録は右パネル(`?panel=<id>`)。
 * ビュー: 自分の担当(`owner=me`)/ 未完了 / 期限超過 / すべて。
 * サーバーに文字検索は無いので、検索語と「期限超過」は手元で絞る(そのときは 200 件まで読む)。
 */
import { useMutation, useQueries } from "@tanstack/react-query";
import { Alert, Button, Input, Modal, Space, Tooltip, Typography, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { WfTask } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import {
  DataTable,
  FilterBar,
  PageHeader,
  PARTIAL_CLEAR_LABEL,
  PartialResultNote,
  partialResultText,
  multiSelectCondition,
  useSearchParam,
  useSearchParamInput,
  useSearchParamsPatch,
  type DataColumn,
} from "../../ui";
import { TaskPanel } from "./TaskPanel";
import { ASSIGNMENT_KINDS, TASK_MODES, TASK_OPS, TASK_STATUSES } from "./constants";
import { errorText } from "./hooks";
import { useDownload } from "./loanHooks";
import { CompleteTask, RerunTask } from "./wfActions";
import { employeeLink, matchesAll, requestLink, taskLoansCell, taskLoanText, taskNameCell, taskStatusCell } from "./wfBits";
import { isOverdue, useInvalidateWf, useTasks, useWfPerms } from "./wfHooks";

const VIEWS = [
  { key: "mine", label: "自分の担当" },
  { key: "pending", label: "未完了" },
  { key: "overdue", label: "期限超過" },
  { key: "all", label: "すべて" },
] as const;
type ViewKey = (typeof VIEWS)[number]["key"];

/** 手元で絞るときに読む件数(期限超過 / 文字検索)。これを超える分は条件で狭めてもらう。 */
const LOCAL_LIMIT = "200";

function viewParams(view: ViewKey): Record<string, string> {
  if (view === "mine") return { owner: "me", status: "pending" };
  if (view === "all") return {};
  return { status: "pending" };
}

/** ビューごとの件数。limit=1 で total だけ読む。期限超過は pending を読んで数える(200 件まで)。 */
function useViewCounts() {
  const api = useApi();
  const results = useQueries({
    queries: [
      ...(["mine", "pending", "all"] as ViewKey[]).map((k) => ({
        queryKey: ["hr", "wf-tasks", "count", k],
        queryFn: () => api.wfTasks(new URLSearchParams({ limit: "1", offset: "0", ...viewParams(k) })),
        select: (d: { total: number }) => d.total,
        staleTime: 60_000,
      })),
      {
        queryKey: ["hr", "wf-tasks", "count", "overdue"],
        queryFn: () => api.wfTasks(new URLSearchParams({ limit: LOCAL_LIMIT, offset: "0", status: "pending" })),
        select: (d: { items: WfTask[] }) => d.items.filter(isOverdue).length,
        staleTime: 60_000,
      },
    ],
  });
  return { mine: results[0].data, pending: results[1].data, all: results[2].data, overdue: results[3].data };
}

export function TasksPage() {
  const api = useApi();
  const download = useDownload();
  const invalidate = useInvalidateWf();
  const { canOperate } = useWfPerms();
  const [sp] = useSearchParams();
  const patch = useSearchParamsPatch();
  const [panelId, setPanelId] = useSearchParam("panel");
  const [qInput, setQInput] = useSearchParamInput("q");
  const q = sp.get("q") ?? "";
  const view = (VIEWS.find((v) => v.key === sp.get("view"))?.key ?? "pending") as ViewKey;
  const csv = (name: string) => (sp.get(name) ?? "").split(",").filter(Boolean);
  const kinds = csv("assignment_kind");
  const ops = csv("op");
  const modes = csv("mode");
  const statuses = csv("status");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selected, setSelected] = useState<number[]>([]);
  const [bulk, setBulk] = useState<"done" | "skip" | null>(null);
  const [note, setNote] = useState("");

  // 複数選択の条件はサーバーが 1 値しか受けないので、1 つだけならサーバー、2 つ以上は手元で絞る
  const serverOne = (vals: string[]) => (vals.length === 1 ? vals[0] : undefined);
  const local = Boolean(q) || view === "overdue" || [kinds, ops, modes, statuses].some((v) => v.length > 1);
  const filters: Record<string, string> = {
    ...viewParams(view),
    ...(serverOne(kinds) ? { assignment_kind: kinds[0] } : {}),
    ...(serverOne(ops) ? { op: ops[0] } : {}),
    ...(serverOne(modes) ? { mode: modes[0] } : {}),
    ...(view === "all" && serverOne(statuses) ? { status: statuses[0] } : {}),
  };
  const query = useTasks(
    local
      ? { ...filters, limit: LOCAL_LIMIT, offset: "0" }
      : { ...filters, limit: String(pageSize), offset: String((page - 1) * pageSize) },
  );
  const counts = useViewCounts();

  // 絞り込みを変えたら選択を捨てる。**一覧から消えた行を選んだまま**一括操作すると、
  // 画面に出ていない人まで完了・対象外にしてしまう
  const filterKey = `${view}|${q}|${kinds}|${ops}|${modes}|${statuses}`;
  useEffect(() => {
    setPage(1);
    setSelected([]);
  }, [filterKey]);

  const rows = useMemo(() => {
    const items = query.data?.items ?? [];
    if (!local) return items;
    return items.filter(
      (t) =>
        (view !== "overdue" || isOverdue(t)) &&
        (kinds.length < 2 || kinds.includes(t.assignment_kind)) &&
        (ops.length < 2 || ops.includes(t.op)) &&
        (modes.length < 2 || modes.includes(t.mode)) &&
        (view !== "all" || statuses.length < 2 || statuses.includes(t.status)) &&
        matchesAll(q, t.id, t.request_id ? `#${t.request_id}` : "", t.employee_name, t.section, t.name, t.owner, t.target, t.trigger, t.op_label, t.detail),
    );
  }, [query.data, local, view, q, kinds, ops, modes, statuses]);
  const total = local ? rows.length : (query.data?.total ?? 0);
  // 手元で絞っていて、取得が上限で切れている(これより古いタスクは対象外。fix1 中4)
  const fetched = query.data?.items.length ?? 0;
  const partial = local && (query.data?.total ?? 0) > Number(LOCAL_LIMIT);

  const runBulk = useMutation({
    mutationFn: () => api.bulkTasks(selected, bulk ?? "skip", note),
    onSuccess: (r) => {
      void message.success(
        `${r.action === "done" ? "完了にした" : "対象外にした"}: ${r.count} 件` +
          (r.ignored.length ? `(もう閉じていた ${r.ignored.length} 件は触らない)` : ""),
      );
      setBulk(null);
      setNote("");
      setSelected([]);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const columns: DataColumn<WfTask>[] = [
    { title: "#", ellipsis: false, dataIndex: "id", width: 110, render: (v: number) => <span className="ui-num">{v}</span> },
    {
      title: "申請・対象者",
      ellipsis: false,
      width: 200,
      render: (_: unknown, t) => (
        <div>
          <div>{requestLink(t.request_id, t.request_kind, t.assignment_kind_label || "申請なし")}</div>
          {t.employee_name && <div className="ui-cell-sub">{employeeLink(t.employee_id, t.employee_name)}</div>}
        </div>
      ),
    },
    {
      title: "内容",
      ellipsis: true,
      onCell: (row) => ({ title: [`${row.section ? `${row.section}/` : ""}${row.name}`, row.action === "deprovision" ? row.action_label : "", row.loan ? "貸与" : "", row.loan_id ? `台帳 #${row.loan_id}` : "", row.op_label, row.target, row.loans == null ? "" : `${row.status === "done" ? "返却した貸与品" : "貸与品"}: ${row.loans.length ? row.loans.map(taskLoanText).join("、") : "なし"}`].filter(Boolean).join("\n") }),
      width: 320,
      render: (_: unknown, t) => (
        <div>
          {taskNameCell(t, () => setPanelId(String(t.id)))}
          {t.target && <div className="ui-cell-sub">{t.target}</div>}
          {taskLoansCell(t)}
        </div>
      ),
    },
    { title: "担当", ellipsis: false, dataIndex: "owner", width: 160 },
    { title: "状態", ellipsis: false, width: 240, render: (_: unknown, t) => taskStatusCell(t) },
    { title: "実行予定日", ellipsis: false, dataIndex: "scheduled_on", width: 120 },
    {
      title: "操作",
      ellipsis: false,
      width: 180,
      render: (_: unknown, t) => (
        <Space size={4} wrap>
          <CompleteTask task={t} />
          <RerunTask task={t} />
        </Space>
      ),
    },
  ];

  const clearAll = () => {
    setQInput("");
    patch({ q: null, assignment_kind: null, op: null, mode: null, status: null }, true);
  };
  // 手元の条件を全部外してサーバー側のページングに戻す(「期限超過」も手元で絞るので「未完了」へ)
  const clearLocal = () => {
    setQInput("");
    patch({ q: null, assignment_kind: null, op: null, mode: null, status: null, ...(view === "overdue" ? { view: null } : {}) }, true);
  };
  const setCsv = (name: string) => (vals: string[]) => patch({ [name]: vals.length ? vals.join(",") : null }, true);
  const conditions = [
    multiSelectCondition({ key: "assignment_kind", label: "割当種別", options: ASSIGNMENT_KINDS, selected: kinds, onChange: setCsv("assignment_kind") }),
    multiSelectCondition({ key: "op", label: "操作", options: TASK_OPS, selected: ops, onChange: setCsv("op") }),
    multiSelectCondition({ key: "mode", label: "方式", options: TASK_MODES, selected: modes, onChange: setCsv("mode") }),
    ...(view === "all"
      ? [multiSelectCondition({ key: "status", label: "状態", options: TASK_STATUSES, selected: statuses, onChange: setCsv("status") })]
      : []),
  ];
  const filtered = Boolean(q) || conditions.some((c) => c.value);

  const csvPath = () => {
    const p = new URLSearchParams(filters);
    const s = p.toString();
    return `/api/hr/tasks.csv${s ? `?${s}` : ""}`;
  };
  const panelTask = panelId ? rows.find((t) => t.id === Number(panelId)) : undefined;

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="タスク"
          description="完了にできるのは hr:operator(自分が承認した申請のタスクは完了にできない)。自動(コネクタ)のタスクは idp が実行し、失敗したものは「再実行」で投げ直せる。動的グループ・組織 OU から生まれたタスクは申請に紐づかない。"
          secondary={
            // CSV はサーバーの条件だけで作る。手元で絞っているときに押すと一覧と違う行が出るので止める(fix1 中3)
            local ? (
              <Tooltip title="文字検索・期限超過・複数選択は CSV に反映されません。条件を外すと出力できます">
                <Button disabled>CSV</Button>
              </Tooltip>
            ) : (
              <Button onClick={() => void download(csvPath())}>CSV</Button>
            )
          }
        />
        <FilterBar
          search={{ value: qInput, onChange: setQInput, placeholder: "工程名 / 対象者 / 担当 / 対象" }}
          views={{
            items: VIEWS.map((v) => ({
              key: v.key,
              label: v.label,
              count: counts[v.key],
              tone: v.key === "overdue" && counts.overdue ? "warning" : undefined,
            })),
            active: view,
            onChange: (key) => patch({ view: key === "pending" ? null : key, status: null }, true),
          }}
          conditions={conditions}
          right={
            filtered ? (
              <Button size="small" onClick={clearAll}>
                条件を外す
              </Button>
            ) : undefined
          }
        />
        {query.isError && (
          <Alert type="error" showIcon message="タスクを取得できなかった" description={errorText(query.error)} />
        )}
        {partial && <PartialResultNote limit={Number(LOCAL_LIMIT)} onClear={clearLocal} />}
        <DataTable<WfTask>
          columns={columns}
          scrollX={columns.reduce((sum, column) => sum + column.width, 0) + (canOperate ? 48 : 0)}
          rows={rows}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={total}
          countLabel={partial ? `${fetched} 件中 ${rows.length} 件` : undefined}
          page={page}
          pageSize={pageSize}
          onPage={(p, s) => {
            setPage(p);
            setPageSize(s);
          }}
          selectedKey={panelId ? Number(panelId) : null}
          onRowClick={(row) => setPanelId(String(row.id))}
          rowTone={(row) => (isOverdue(row) ? "danger" : undefined)}
          rowSelection={
            canOperate
              ? { columnWidth: 48, selectedRowKeys: selected, onChange: (keys) => setSelected(keys.map(Number)) }
              : undefined
          }
          barLeft={
            canOperate && selected.length > 0 ? (
              <Space wrap>
                <Typography.Text>{selected.length} 件を選んでいる</Typography.Text>
                <Button size="small" onClick={() => setBulk("done")}>
                  一括完了
                </Button>
                <Button size="small" onClick={() => setBulk("skip")}>
                  一括スキップ
                </Button>
                <Button size="small" type="text" onClick={() => setSelected([])}>
                  選択を外す
                </Button>
              </Space>
            ) : undefined
          }
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          empty={{
            text: `${filtered ? "条件に合うタスクはない" : view === "mine" ? "自分が担当する未完了のタスクはない" : "タスクはない"}${
              partial ? `(${partialResultText(Number(LOCAL_LIMIT))})` : ""
            }`,
            action: partial ? (
              <Button onClick={clearLocal}>{PARTIAL_CLEAR_LABEL}</Button>
            ) : filtered ? (
              <Button onClick={clearAll}>条件を外す</Button>
            ) : undefined,
          }}
        />
      </div>
      {panelId && <TaskPanel id={Number(panelId)} task={panelTask} onClose={() => setPanelId(null)} />}

      <Modal
        open={bulk !== null}
        title={bulk === "done" ? "まとめて完了にする" : "まとめて対象外にする"}
        okText={bulk === "done" ? "完了にする" : "対象外にする"}
        cancelText="やめる"
        confirmLoading={runBulk.isPending}
        onCancel={() => setBulk(null)}
        onOk={() => runBulk.mutate()}
        destroyOnClose
      >
        <Typography.Paragraph>
          {selected.length} 件を{bulk === "done" ? "完了" : "対象外"}にする。もう閉じているものは触らない。
          自動実行を待っているタスクが混ざっていると 1 件も変えずに断る(結果が後から返ってくるため)。
        </Typography.Paragraph>
        <Input.TextArea
          rows={3}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="結果に残すメモ(省略可)"
        />
      </Modal>
    </div>
  );
}
